// @ts-nocheck
import * as React from "react";
import { Sidebar, Topbar, AppFooter, useCurrentUser, Ic, AvatarImg, apiErrorMessage } from "./common";
import { CaseCreateModal } from "../components/CaseCreateModal";
import { usePermissionsFromSession } from "../components/permissions";
import { api } from "../api";

// 케이스(어르신) 리스트 — 역할에 따라 범위가 다르다.
//  · 매니저:   본인 담당 케이스(cases#index?care_manager_id={본인 id}).
//  · 권역/시스템 운영자: 권역 전체 케이스(care_manager 필터 없이 — 서버가 region_id로 스코프).
// 권역 대시보드 펑널에서 단계 클릭 → CaseFlowDrilldownModal → '전체 케이스 목록'으로
// 이 화면에 ?status={단계}로 진입하면 해당 상태로 필터가 미리 잡힌다.

// 종결(closed)은 이 목록에서 완전히 제외하므로 라벨에 두지 않는다 — 드롭다운 옵션·
// ?status= 검증에서 자동 제외되고, 종결 케이스는 fetch 단계에서 걸러져 배지로도 안 뜬다.
// (종결 이력은 권역 대시보드 펑널 '종료' 드릴다운 모달·어르신 상세·리포트에서 확인)
const STATUS_LABEL = {
  applying: "보류", matching: "매칭중",
  assigned: "배정", caring: "케어중",
};
const STATUS_COLOR = {
  applying: "gray", matching: "orange",
  assigned: "sky", caring: "green", closed: "gray",
};
const PRIORITY_LABEL = { critical: "높음", warning: "보통", normal: "낮음" };
const PRIORITY_COLOR = { critical: "rose", warning: "amber", normal: "gray" };
const RISK_LABEL  = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR  = { critical: "rose", warning: "amber", stable: "green" };

// 종결된 케이스는 기본 숨김 — "담당"의 일반적 의미는 진행 중인 건.
const ACTIVE_STATUSES = ["applying", "matching", "assigned", "caring"];

// 권역 전체 뷰의 페이지 크기(서버 cases#index PER_PAGE_MAX 이내).
const PER_PAGE = 50;

function MgrCases() {
  const { user, loaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded });
  // 매니저(또는 미로그인 데모)는 "내 담당" 뷰, 그 외 직원은 "권역 전체" 뷰.
  const managerView = user ? user.role === "manager" : true;
  // 펑널 드릴다운에서 ?status=caring 등으로 진입하면 그 상태로 초기 필터.
  const initialStatus = React.useMemo(() => {
    const s = new URLSearchParams(window.location.search).get("status");
    // closed/all 로 들어와도(펑널 '종료' 모달의 전체보기 링크 등) 진행 중 목록으로 — 종결은 숨김.
    return s && STATUS_LABEL[s] ? s : "active";
  }, []);
  const [cases, setCases] = React.useState(null);
  const [err, setErr]   = React.useState(null);
  const [q, setQ]       = React.useState("");
  const [statusFilter, setStatusFilter]     = React.useState(initialStatus); // "active" | "all" | 특정 상태
  // 12단계 흐름 위젯 '긴급돌봄' 등에서 ?priority=critical 로 들어오면 그 우선순위로 초기 필터.
  const initialPriority = React.useMemo(() => {
    const p = new URLSearchParams(window.location.search).get("priority");
    return p && PRIORITY_LABEL[p] ? p : "all";
  }, []);
  const [priorityFilter, setPriorityFilter] = React.useState(initialPriority); // "all" | critical | warning | normal
  const [createOpen, setCreateOpen] = React.useState(false);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [busyId, setBusyId] = React.useState(null);      // 매칭 전환 중인 케이스 id
  const [actionErr, setActionErr] = React.useState(null); // 매칭 전환 실패 메시지
  // 운영자(권역 전체) 뷰는 서버 페이지네이션을 쓴다 — 권역 케이스가 수백 건이라
  // 예전의 bare 100건 상한에서는 나머지가 어떤 필터로도 도달 불가였다.
  // 매니저 본인 뷰는 담당 건수가 작아 기존 bare 응답을 그대로 쓴다.
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  const [serverCounts, setServerCounts] = React.useState(null);
  // 검색어는 서버로 보내므로 타이핑마다 치지 않도록 350ms 디바운스.
  const [dq, setDq] = React.useState("");
  React.useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 350); return () => clearTimeout(t); }, [q]);

  // 보류(applying) 케이스를 목록에서 바로 AI 자동매칭(COD) 큐로 전환.
  // 드릴다운 모달과 동일하게 PATCH cases/:id status=matching → 백엔드가 후보 매니저
  // 자동 생성. 성공 후 재조회하면 배지가 '매칭중'으로 갱신된다.
  const startMatching = async (caseId) => {
    setActionErr(null);
    setBusyId(caseId);
    try {
      await api.update("cases", caseId, { status: "matching" });
      setRefreshTick(t => t + 1);
    } catch (e) {
      setActionErr(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  // 매니저: 본인 care_manager_id로 필터. 미로그인 데모는 "이미경" 집합으로 빈 화면 회피.
  // 운영자(권역/시스템): care_manager 필터 없이 권역 전체 — 서버가 region_id로 스코프한다.
  React.useEffect(() => {
    if (!loaded) return;
    (async () => {
      try {
        // 종결(closed)은 이 화면에서 완전히 제외 — 서버에서 걸러(exclude_closed)
        // 페이지마다 행 수가 들쭉날쭉해지지 않게 한다.
        if (!managerView) {
          const query = { exclude_closed: "true", page: String(page), per_page: String(PER_PAGE) };
          if (statusFilter !== "active" && statusFilter !== "all") query.status = statusFilter;
          if (priorityFilter !== "all") query.priority = priorityFilter;
          if (dq) query.q = dq;
          const data = await api.list("cases", query);
          // 페이지 파라미터를 보내면 객체 응답. 배열이 오면(구버전 서버) 방어적으로 수용.
          if (Array.isArray(data)) {
            setCases(data.filter(c => c.status !== "closed"));
            setPagination({ page: 1, total: data.length, total_pages: 1 });
            setServerCounts(null);
          } else {
            setCases(data.cases || []);
            setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
            setServerCounts(data.counts || null);
          }
          return;
        }
        let mid = user?.care_manager_id;
        if (!mid) {
          const list = await api.list("care_managers");
          mid = (list.find(m => m.name === "이미경") || list[0])?.id;
        }
        if (!mid) { setCases([]); return; }
        const rows = await api.list("cases", { care_manager_id: String(mid), exclude_closed: "true" });
        setCases(rows.filter(c => c.status !== "closed"));
      } catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, [loaded, managerView, user?.care_manager_id, refreshTick, page, statusFilter, priorityFilter, dq]);

  // 필터·검색이 바뀌면 1페이지로 되돌린다(빈 페이지 방지).
  React.useEffect(() => { setPage(1); }, [statusFilter, priorityFilter, dq]);
  // 필터 결과가 줄어 현재 페이지가 범위를 벗어나면 마지막 페이지로 보정.
  React.useEffect(() => {
    if (pagination.total_pages > 0 && page > pagination.total_pages) setPage(pagination.total_pages);
  }, [pagination.total_pages, page]);

  // 운영자 뷰는 상태·우선순위·검색을 서버에서 적용하므로 여기선 그대로 쓴다.
  // 매니저 본인 뷰만 bare 응답에 클라이언트 필터를 건다.
  const filtered = React.useMemo(() => {
    if (!cases) return [];
    if (!managerView) return cases;
    const needle = q.trim();
    return cases.filter(c => {
      if (statusFilter === "active" && !ACTIVE_STATUSES.includes(c.status)) return false;
      if (statusFilter !== "active" && statusFilter !== "all" && c.status !== statusFilter) return false;
      if (priorityFilter !== "all" && c.priority !== priorityFilter) return false;
      if (needle && !c.elderly_name.includes(needle)) return false;
      return true;
    });
  }, [cases, managerView, q, statusFilter, priorityFilter]);

  // KPI 카드는 "현재 페이지"가 아니라 필터 전체 기준이어야 한다 — 서버가 준
  // facet 카운트(상태/우선순위 필터를 제외한 분포)를 우선 쓰고, bare 응답
  // (매니저 뷰)에서만 로드된 배열로 센다.
  const counts = React.useMemo(() => {
    if (serverCounts) {
      const st = serverCounts.by_status || {};
      const pr = serverCounts.by_priority || {};
      return {
        total:    serverCounts.total ?? 0,
        critical: pr.critical ?? 0,
        caring:   st.caring ?? 0,
        matching: st.matching ?? 0,
      };
    }
    const base = { total: 0, critical: 0, caring: 0, matching: 0 };
    if (!cases) return base;
    for (const c of cases) {
      base.total++;
      if (c.priority === "critical") base.critical++;
      if (c.status === "caring")   base.caring++;
      if (c.status === "matching") base.matching++;
    }
    return base;
  }, [cases, serverCounts]);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="manager" active="cases"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title={managerView ? "내 담당 어르신" : "권역 케이스 목록"} sub={`전체 ${counts.total.toLocaleString()}명 · 높음 ${counts.critical.toLocaleString()}건 · 케어중 ${counts.caring.toLocaleString()}건`}>
            {perms.canManageCases && (
              <button className="btn btn-primary btn-md" onClick={()=>setCreateOpen(true)}>{Ic.plus} 신규 케이스 등록</button>
            )}
          </Topbar>
          <div className="main">

            <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:14}}>
              {/*
                KPI 카드 = 원터치 필터 버튼. 클릭하면 하단 status/priority select도 함께 갱신되어
                현재 어느 필터가 활성인지 카드에도 오렌지 액센트 테두리로 즉시 표시.
                이미 활성인 카드를 한 번 더 누르면 '전체'로 초기화 (토글).
              */}
              {[
                { l:"전체",   v:counts.total,    c:"ink-1",      status:"active", priority:"all"      },
                { l:"높음",   v:counts.critical, c:"rose-600",   status:"active", priority:"critical" },
                { l:"케어중", v:counts.caring,   c:"green-600",  status:"caring", priority:"all"      },
                { l:"매칭중", v:counts.matching, c:"orange-600", status:"matching", priority:"all"    },
              ].map((s,i) => {
                // 현재 필터 조합이 이 카드가 대표하는 상태와 일치하면 활성.
                const isActive = statusFilter === s.status && priorityFilter === s.priority;
                const handleClick = () => {
                  if (isActive) {
                    // 이미 활성 카드 재클릭 → '전체' 초기화 (토글 UX).
                    setStatusFilter("active"); setPriorityFilter("all");
                  } else {
                    setStatusFilter(s.status); setPriorityFilter(s.priority);
                  }
                };
                return (
                  <div key={i}
                    role="button" tabIndex={0} aria-pressed={isActive}
                    title={isActive ? `${s.l} 필터 해제` : `${s.l} 만 보기`}
                    onClick={handleClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
                    className="card"
                    style={{
                      padding:'14px 16px',
                      cursor:'pointer',
                      border: isActive ? `2px solid var(--${s.c})` : '1px solid var(--border-soft)',
                      boxShadow: isActive ? `0 4px 12px -4px var(--${s.c})` : undefined,
                      transition: 'border-color .15s ease, box-shadow .15s ease, transform .12s ease',
                    }}
                    onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'none'; }}
                  >
                    <div className="muted" style={{fontSize:11, fontWeight:600, display:'flex', alignItems:'center', gap:6}}>
                      {s.l}
                      {isActive && <span style={{fontSize:9, color:`var(--${s.c})`, fontWeight:800}}>● 활성</span>}
                    </div>
                    <div style={{fontSize:24, fontWeight:800, color:`var(--${s.c})`, fontVariantNumeric:'tabular-nums'}}>{s.v.toLocaleString()}</div>
                  </div>
                );
              })}
            </div>

            <div className="card" style={{padding:'14px 18px', marginBottom:12, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'1 1 240px'}}>
                {Ic.search}
                <input className="input" placeholder="어르신 이름 검색" value={q} onChange={e => setQ(e.target.value)}/>
              </div>
              <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{maxWidth:160}}>
                <option value="active">전체</option>
                {Object.entries(STATUS_LABEL).map(([k,l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select className="input" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={{maxWidth:140}}>
                <option value="all">전체 우선순위</option>
                {Object.entries(PRIORITY_LABEL).map(([k,l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <div style={{flex:1}}/>
              <span className="muted" style={{fontSize:12}}>
                {managerView
                  ? `${filtered.length.toLocaleString()}건 표시`
                  : `필터 적용 ${pagination.total.toLocaleString()}건 · 페이지 ${pagination.page}/${Math.max(pagination.total_pages, 1)}`}
              </span>
            </div>

            {err && <div className="card" style={{padding:16, color:'var(--rose-500)'}}>불러오기 실패: {err}</div>}

            {actionErr && <div className="card" style={{padding:'12px 16px', marginBottom:12, color:'var(--rose-500)', fontSize:13}}>매칭 전환 실패: {actionErr}</div>}

            {!err && cases === null && <div className="muted" style={{padding:30}}>불러오는 중…</div>}

            {!err && cases && (
              <div className="card" style={{overflow:'hidden'}}>
                <div style={{display:'grid', gridTemplateColumns:'1.6fr 0.6fr 1.5fr 0.8fr 0.8fr 0.8fr 1fr 0.5fr', gap:12, padding:'12px 18px', fontSize:11, fontWeight:700, color:'var(--ink-3)', background:'var(--bg-sub)', borderBottom:'1px solid var(--border-soft)'}}>
                  <div>어르신</div>
                  <div>나이</div>
                  <div>주소</div>
                  <div>위험도</div>
                  <div>케이스</div>
                  <div>우선순위</div>
                  <div>액션</div>
                  <div style={{textAlign:'right'}}>상세</div>
                </div>
                {filtered.length === 0 && (
                  <div className="muted" style={{padding:'40px 18px', textAlign:'center', fontSize:13}}>
                    {cases.length === 0
                      ? (managerView ? "담당 어르신이 아직 없습니다." : "권역에 등록된 케이스가 없습니다.")
                      : "조건에 맞는 어르신이 없습니다."}
                  </div>
                )}
                {filtered.map((c, i) => (
                  <a key={c.id} href={`/case?elderly_id=${c.elderly_id}`}
                    style={{display:'grid', gridTemplateColumns:'1.6fr 0.6fr 1.5fr 0.8fr 0.8fr 0.8fr 1fr 0.5fr', gap:12, padding:'14px 18px', borderTop: i>0?'1px solid var(--border-soft)':'none', alignItems:'center', textDecoration:'none', color:'inherit', cursor:'pointer'}}>
                    <div style={{display:'flex', alignItems:'center', gap:10, minWidth:0}}>
                      <AvatarImg className="avatar sm" url={c.elderly_avatar_url} name={c.elderly_name}/>
                      <div style={{fontSize:13.5, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.elderly_name} 어르신</div>
                    </div>
                    <div style={{fontSize:12.5, color:'var(--ink-2)'}}>{c.elderly_age != null ? `${c.elderly_age}세` : '-'}</div>
                    <div className="muted" style={{fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.elderly_address || '-'}</div>
                    <div>
                      {c.elderly_risk_level
                        ? <span className={"badge badge-" + RISK_COLOR[c.elderly_risk_level]} style={{fontSize:10}}>{RISK_LABEL[c.elderly_risk_level]}</span>
                        : <span className="muted" style={{fontSize:11}}>-</span>}
                    </div>
                    <div><span className={"badge badge-" + STATUS_COLOR[c.status]} style={{fontSize:10}}>{STATUS_LABEL[c.status]}</span></div>
                    <div><span className={"badge badge-" + PRIORITY_COLOR[c.priority]} style={{fontSize:10}}>{PRIORITY_LABEL[c.priority]}</span></div>
                    <div onClick={(e)=>e.stopPropagation()}>
                      {perms.canManageCases && c.status === "applying" && (
                        <span role="button" tabIndex={0}
                          className="btn btn-primary btn-sm"
                          aria-disabled={busyId === c.id}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); if (busyId !== c.id) startMatching(c.id); }}
                          style={busyId === c.id ? {opacity:0.6, pointerEvents:'none'} : undefined}
                          title="이 케이스를 AI 자동매칭(COD) 큐로 보냅니다 (후보 매니저 자동 생성)">
                          {busyId === c.id ? '전환 중…' : '매칭 시작'}
                        </span>
                      )}
                      {perms.canManageCases && c.status === "matching" && (
                        <a href={`/cod?case_id=${c.id}`}
                          className="btn btn-ghost btn-sm"
                          onClick={(e)=>e.stopPropagation()}
                          title="AI 자동매칭 인박스에서 이 케이스의 후보 매니저를 확인·배정합니다">
                          COD 열기
                        </a>
                      )}
                    </div>
                    <div style={{textAlign:'right', color:'var(--ink-3)'}}>{Ic.arrow}</div>
                  </a>
                ))}
                {filtered.length === 0 && (
                  <div className="muted" style={{padding:'40px 18px', textAlign:'center', fontSize:13}}>
                    조건에 맞는 케이스가 없습니다.
                  </div>
                )}
              </div>
            )}

            {/* 권역 전체 뷰만 페이지네이션 — 매니저 본인 뷰는 전건이 한 화면에 들어온다. */}
            {!err && cases && !managerView && pagination.total_pages > 1 && (
              <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'14px 0'}}>
                <button className="btn btn-ghost btn-sm" disabled={pagination.page <= 1}
                  onClick={()=>setPage(p => Math.max(1, p - 1))}>이전</button>
                <span className="muted" style={{fontSize:12}}>
                  {pagination.page} / {pagination.total_pages} 페이지 · 전체 {pagination.total.toLocaleString()}건
                </span>
                <button className="btn btn-ghost btn-sm" disabled={pagination.page >= pagination.total_pages}
                  onClick={()=>setPage(p => Math.min(pagination.total_pages, p + 1))}>다음</button>
              </div>
            )}

          </div>
          <AppFooter/>
        </div>
      </div>
      {createOpen && (
        <CaseCreateModal
          onClose={()=>setCreateOpen(false)}
          onCreated={()=>{ setCreateOpen(false); setRefreshTick(t=>t+1); }}
        />
      )}
    </div>
  );
}

export { MgrCases };
